import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Modal from 'components/atoms/Modal'
import styled from 'styled-components'
import { Tabs, Tab, Button, List, ListItem, Checkbox } from '@mui/material'
import { selectFilesSharedUsers, selectBulkUnshareStatus } from 'features/bpDetails/ducks/selectors'
import { bulkUnsharedFilesRoutine, fetchSharedUsersForFilesRoutine } from '../ducks/actions'

const BulkUnsharedFilesModal = ({fileIds = [], open, onClose, selectedFiles, bp: bpProp = null }) => {
  const safeSelectedFiles = selectedFiles || fileIds || []
  const [tabIndex, setTabIndex] = useState(0)
  const [selectedInternalIds, setSelectedInternalIds] = useState([])
  const [selectedExternalIds, setSelectedExternalIds] = useState([])
  const selectedBpId = useSelector(state => state.bpDetails.selectedBp?.id)
  const bpId = bpProp?.id ?? selectedBpId

  const { loading } = useSelector(selectBulkUnshareStatus)
  const { data: { internalUsers, externalShares } = {} } = useSelector(selectFilesSharedUsers)

  const dispatch = useDispatch()

  const handleTabChange = (_, newIndex) => setTabIndex(newIndex)

  const handleToggleInternal = (userId) => {
    setSelectedInternalIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    )
  }

  const handleToggleExternal = (shareId) => {
    setSelectedExternalIds(prev =>
      prev.includes(shareId) ? prev.filter(id => id !== shareId) : [...prev, shareId]
    )
  }

  const handleUnshare = () => {
    const payload = {
      bp_id: bpId,
      fileIds: safeSelectedFiles,
      internals: selectedInternalIds,
      externals: selectedExternalIds
    }

    dispatch(bulkUnsharedFilesRoutine.trigger(payload))
    onClose()
  }

  useEffect(() => {
    if (open && safeSelectedFiles.length > 0) {
      dispatch(fetchSharedUsersForFilesRoutine.trigger(safeSelectedFiles))
    }
  }, [open, safeSelectedFiles, dispatch])

  return (
    <Modal open={open} onClose={onClose} title="Unshare selected files">
      <Wrapper>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ display: { xs: 'none', md: 'flex' } }}
        >
          <Tab label="Internal users" />
          <Tab label="External shares" />
        </Tabs>

        <SegmentedTabs>
          <SegmentButton
            type="button"
            $active={tabIndex === 0}
            onClick={() => setTabIndex(0)}
          >
            Internal users
          </SegmentButton>
          <SegmentButton
            type="button"
            $active={tabIndex === 1}
            onClick={() => setTabIndex(1)}
          >
            External shares
          </SegmentButton>
        </SegmentedTabs>

        {tabIndex === 0 && (
          <TabContent>
            {internalUsers.length === 0 ? (
              <EmptyStateText>No internal shares found.</EmptyStateText>
            ) : (
              <StyledList dense>
                {internalUsers.map(user => {
                  const isChecked = selectedInternalIds.includes(user.id)
                  return (
                    <StyledListItem key={user.id} onClick={() => handleToggleInternal(user.id)}>
                      <Checkbox edge="start" checked={isChecked} tabIndex={-1} disableRipple />
                      <UserInfo>
                        <div className="name">{user.firstName} {user.lastName}</div>
                        <div className="email">{user.email}</div>
                      </UserInfo>
                    </StyledListItem>
                  )
                })}
              </StyledList>
            )}
          </TabContent>
        )}

        {tabIndex === 1 && (
          <TabContent>
            {externalShares.length === 0 ? (
              <EmptyStateText>No external shares found.</EmptyStateText>
            ) : (
              <StyledList dense>
                {externalShares.map(external => {
                  const isChecked = selectedExternalIds.includes(external.id)
                  return (
                    <StyledListItem key={external.id} onClick={() => handleToggleExternal(external.id)}>
                      <Checkbox edge="start" checked={isChecked} tabIndex={-1} disableRipple />
                      <UserInfo>
                        <div className="email">{external.email}</div>
                      </UserInfo>
                    </StyledListItem>
                  )
                })}
              </StyledList>
            )}
          </TabContent>
        )}

        <Footer>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleUnshare}
            disabled={loading || (selectedInternalIds.length === 0 && selectedExternalIds.length === 0)}
          >
            {loading ? 'Unsharing...' : 'Unshare'}
          </Button>
        </Footer>
      </Wrapper>
    </Modal>
  )
}

export default BulkUnsharedFilesModal

// styled-components (rămân neschimbate):
const Wrapper = styled.div`
  padding: 20px;

  @media (max-width: 768px) {
    padding: 12px 0 0;
  }
`
const SegmentedTabs = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
    padding: 0 12px 12px;
  }
`

const SegmentButton = styled.button`
  border: 1px solid ${({ $active }) => ($active ? '#C24A2E' : '#e5e7eb')};
  background: ${({ $active }) => ($active ? '#fff5f2' : '#fff')};
  color: ${({ $active }) => ($active ? '#C24A2E' : '#6b7280')};
  font-size: 12px;
  font-weight: 700;
  padding: 8px 6px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`
const TabContent = styled.div`
  margin-top: 20px;
  max-height: 300px;
  overflow-y: auto;

  @media (max-width: 768px) {
    margin-top: 12px;
    max-height: 50vh;
  }
`
const StyledList = styled(List)`
  max-height: 300px;
  overflow-y: auto;

  @media (max-width: 768px) {
    max-height: 50vh;
  }
`
const StyledListItem = styled(ListItem)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background-color: #f1f1f1; }

  @media (max-width: 768px) {
    padding: 10px 8px;
  }
`
const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  .name { font-weight: 500; }
  .email { font-size: 13px; color: #666; }
`
const Footer = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    position: sticky;
    bottom: 0;
    background: #fff;
    padding: 12px 0 16px;
    margin-top: 12px;
    justify-content: stretch;

    button {
      width: 100%;
      justify-content: center;
    }
  }
`
const EmptyStateText = styled.div`margin: 20px 0; text-align: center; color: #888; font-size: 14px;`
